import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { AdminDraft } from '../../_shared/domain/administrator-draft.model';
import type { AddAdministratorError } from '../domain/add-administrator-error';
import { initialState } from './add-administrator.state';

/**
 * Add-administrator slice. Reducers are pure state transitions only; every side
 * effect (validation trigger, the invitation POST) lives in the saga, which
 * dispatches the `save*` transitions below as it progresses. This is what lets
 * the reducers be unit-tested as `(state, action) => state` with zero mocks.
 */
const slice = createSlice({
  name: 'addAdministrator',
  initialState,
  reducers: {
    /** A single field changed; clears that field's validation error. */
    fieldChanged: (
      state,
      action: PayloadAction<{ field: keyof AdminDraft; value: string }>,
    ) => {
      const { field, value } = action.payload;
      // `role` is a union (AdminRole | ''); the select only ever emits valid
      // values, so we write through a string view of the draft.
      (state.draft as Record<keyof AdminDraft, string>)[field] = value;
      delete state.validationErrors[field];
    },
    // ── Save lifecycle (dispatched by the saga) ──
    saveStarted: (state) => {
      state.saveStatus = 'saving';
      state.error = null;
      state.validationErrors = {};
    },
    saveSucceeded: (state) => {
      state.saveStatus = 'succeeded';
    },
    saveFailed: (state, action: PayloadAction<AddAdministratorError>) => {
      state.saveStatus = 'failed';
      state.error = action.payload;
      if (action.payload.kind === 'validation') {
        state.validationErrors = action.payload.fields;
      }
    },
    /** Reset everything to the initial state (after success / on unmount). */
    resetForm: () => initialState,
  },
});

export const addAdministratorActions = slice.actions;
export const addAdministratorReducer = slice.reducer;
